/* import __COLOCATED_TEMPLATE__ from './out-of-stock-condition.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ImportSource from 'embercom/models/import-service/import-source';

interface Signature {
  Args: {
    modal: any;
    importSource: ImportSource;
    onModalClose: () => void;
  };
  Element: null;
}

export default class ShopifyOutOfStockCondition extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: any;

  @tracked key = '';
  @tracked operator: '==' | '!=' = '==';
  @tracked value = '';
  @tracked isSaving = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    let condition = args.importSource?.outOfStockCondition;
    if (condition) {
      this.key = condition.key;
      this.operator = condition.operator;
      this.value = condition.value;
    }
  }

  get operatorOptions() {
    return [
      {
        text: this.intl.t(
          'knowledge-hub.sync-modal.shopify.out-of-stock-condition.operator-equals',
        ),
        value: '==',
      },
      {
        text: this.intl.t(
          'knowledge-hub.sync-modal.shopify.out-of-stock-condition.operator-not-equals',
        ),
        value: '!=',
      },
    ];
  }

  get isSaveDisabled() {
    return !this.key.trim() || !this.value.trim() || this.isSaving;
  }

  @action
  updateKey(event: InputEvent) {
    this.key = (event.target as HTMLInputElement).value;
  }

  @action
  updateValue(event: InputEvent) {
    this.value = (event.target as HTMLInputElement).value;
  }

  @action
  updateOperator(value: '==' | '!=') {
    this.operator = value;
  }

  @action
  async saveCondition() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updateImportSourceWithParam(this.args.importSource.id, {
        shopify_context: {
          out_of_stock_condition: {
            key: this.key.trim(),
            operator: this.operator,
            value: this.value.trim(),
          },
        },
      });
      await this.args.importSource.reload();
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-success'),
      );
      this.args.onModalClose();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action
  async clearCondition() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updateImportSourceWithParam(this.args.importSource.id, {
        shopify_context: {
          out_of_stock_condition: null,
        },
      });
      await this.args.importSource.reload();
      this.key = '';
      this.operator = '==';
      this.value = '';
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.clear-success'),
      );
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify::OutOfStockCondition': typeof ShopifyOutOfStockCondition;
    'knowledge-hub/setup-modal/sync-modal/shopify/out-of-stock-condition': typeof ShopifyOutOfStockCondition;
  }
}
